import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  ChartBarIcon,
  ServerStackIcon,
  CubeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { Project, Environment, Deployment, EnvironmentStatus } from '../types/api';
import Logger from '../utils/logger';

import ApplicationStatus from '../components/StatusMonitoring/ApplicationStatus';

interface ProjectWithEnvironments extends Project {
  environments: EnvironmentWithDeployments[];
}

interface EnvironmentWithDeployments extends Environment {
  deployments: Deployment[];
  status?: EnvironmentStatus | null;
}

interface ApplicationSummary {
  total: number;
  running: number;
  failed: number;
  pending: number;
}

const StatusDashboard: React.FC = () => {
  const { t } = (useTranslation as any)('status');
  const [projects, setProjects] = useState<ProjectWithEnvironments[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [applicationSummary, setApplicationSummary] = useState<ApplicationSummary>({
    total: 0,
    running: 0,
    failed: 0,
    pending: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Charger tous les projets
      const projectsData = await apiService.getProjects();
      
      // Pour chaque projet, charger ses environnements
      const projectsWithEnvironments = await Promise.all(
        projectsData.map(async (project) => {
          try {
            const environments = await apiService.getProjectEnvironments(project.id);
            
            // Pour chaque environnement, charger les déploiements et le status
            const environmentsWithDeployments = await Promise.all(
              environments.map(async (environment) => {
                try {
                  const [deployments, status] = await Promise.all([
                    apiService.getEnvironmentDeployments(environment.id),
                    apiService.getEnvironmentStatus(environment.id).catch(() => null)
                  ]);
                  
                  // Le statut vient DEJA de la liste ci-dessus : `status` de
                  // `getDeploymentDetails` n'est que `deployment.statut_deploiement`
                  // relu dans la meme colonne (`deployments.py`, "status":
                  // deployment.statut_deploiement.value).
                  //
                  // Cette page appelait pourtant `getDeploymentDetails` par
                  // deploiement, en parallele et sans limite -- ~58 requetes pour
                  // une valeur deja en main. Or cet endpoint lit le values.yaml sur
                  // GitLab en tenant sa session de base ouverte : les 25
                  // emplacements de pgbouncer saturaient, la boucle d'evenements de
                  // l'API se bloquait, et la sonde de vivacite tuait les deux
                  // repliques. Panne de production du 2026-09-13 13:55.
                  return {
                    ...environment,
                    deployments,
                    status
                  };
                } catch (error) {
                  Logger.warn(`Erreur chargement environnement ${environment.id}:`, error);
                  return {
                    ...environment,
                    deployments: [],
                    status: undefined
                  };
                }
              })
            );

            return {
              ...project,
              environments: environmentsWithDeployments
            };
          } catch (error) {
            Logger.warn(`Erreur chargement projet ${project.id}:`, error);
            return {
              ...project,
              environments: []
            };
          }
        })
      );

      setProjects(projectsWithEnvironments as ProjectWithEnvironments[]);
      calculateApplicationSummary(projectsWithEnvironments as ProjectWithEnvironments[]);

    } catch (error: unknown) {
      Logger.error('Erreur chargement dashboard:', error);
      toast.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const calculateApplicationSummary = (projectsData: ProjectWithEnvironments[]) => {
    let total = 0;
    let running = 0;
    let failed = 0;
    let pending = 0;

    projectsData.forEach(project => {
      project.environments.forEach(environment => {
        environment.deployments.forEach(deployment => {
          total++;
          
          // Use the individual deployment status instead of environment status
          switch (deployment.statut_deploiement) {
            case 'RUNNING':
            case 'DEPLOYED':
            case 'HEALTHY':
              running++;
              break;
            case 'FAILED':
              failed++;
              break;
            case 'PENDING':
            case 'BUILDING':
            default:
              pending++;
              break;
          }
        });
      });
    });

    setApplicationSummary({ total, running, failed, pending });
  };

  const refreshDashboard = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
    toast.success(t('refreshed'));
  };

  const getEnvironmentStatusIcon = (environment: EnvironmentWithDeployments) => {
    if (!environment.status) {
      return <ClockIcon className="h-5 w-5 text-text-quaternary" />;
    }

    if (environment.status.status === 'active' && environment.status.gitlab_config === 'created') {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    } else if (environment.status.status === 'failed') {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    } else {
      return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
        <p className="mt-4 text-text-tertiary">{t('loading')}</p>
      </div>
    );
  }

  return (
    <>
          {/* Header */}
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-text-primary sm:text-3xl sm:truncate">
                {t('title')}
              </h2>
              <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                <div className="mt-2 flex items-center text-sm text-text-tertiary">
                  <ChartBarIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-text-quaternary" />
                  {t('subtitle')}
                </div>
              </div>
            </div>
            <div className="mt-5 flex lg:mt-0 lg:ml-4">
              <button
                onClick={refreshDashboard}
                disabled={refreshing}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-card text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed "
              >
                <ArrowPathIcon className={`-ml-1 mr-2 h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? t('refreshing') : t('refresh')}
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CubeIcon className="h-6 w-6 text-text-quaternary" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-text-tertiary truncate">
                        {t('summary.totalApps')}
                      </dt>
                      <dd className="text-lg font-medium text-text-primary">
                        {applicationSummary.total}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-text-tertiary truncate">
                        {t('summary.running')}
                      </dt>
                      <dd className="text-lg font-medium text-green-600">
                        {applicationSummary.running}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-6 w-6 text-red-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-text-tertiary truncate">
                        {t('summary.failed')}
                      </dt>
                      <dd className="text-lg font-medium text-red-600">
                        {applicationSummary.failed}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-6 w-6 text-yellow-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-text-tertiary truncate">
                        {t('summary.pending')}
                      </dt>
                      <dd className="text-lg font-medium text-yellow-600">
                        {applicationSummary.pending}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Projects and Applications */}
          <div className="mt-8">
              <div className="space-y-8">
                {projects.length === 0 ? (
                  <div className="text-center py-12">
                    <CubeIcon className="mx-auto h-12 w-12 text-text-quaternary" />
                    <h3 className="mt-2 text-sm font-medium text-text-primary">{t('empty.title')}</h3>
                    <p className="mt-1 text-sm text-text-tertiary">
                      {t('empty.subtitle')}
                    </p>
                    <div className="mt-6">
                      <Link
                        to="/projects"
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-card text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700"
                      >
                        {t('empty.createProject')}
                      </Link>
                    </div>
                  </div>
                ) : (
                  projects.map((project) => (
                  <div key={project.id} className="bg-surface-2 shadow-card rounded-card">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <ServerStackIcon className="h-6 w-6 text-accent-500 mr-3" />
                          <div>
                            <h3 className="text-lg leading-6 font-medium text-text-primary">
                              {project.nom}
                            </h3>
                            {project.description && (
                              <p className="mt-1 text-sm text-text-tertiary">{project.description}</p>
                            )}
                          </div>
                        </div>
                        <Link
                          to={`/environments/${project.id}`}
                          className="inline-flex items-center px-4 py-2 bg-accent-600 text-white text-sm font-medium rounded-lg hover:bg-accent-700 transition-colors shadow-card hover:shadow-md"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"/>
                          </svg>
                          {t('manage')}
                        </Link>
                      </div>

                      {project.environments.length === 0 ? (
                        <div className="mt-4 py-6 flex items-center justify-between px-4 bg-surface-0 rounded-lg">
                          <p className="text-sm text-text-tertiary">{t('noEnvInProject')}</p>
                          <Link
                            to={`/environments/${project.id}`}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-accent-400 hover:text-accent-300 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            {t('createEnvironment')}
                          </Link>
                        </div>
                      ) : (
                        <div className="mt-6 space-y-6">
                          {project.environments.map((environment) => (
                            <div key={environment.id} className="border-l-2 border-border-1 pl-4">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center">
                                  {getEnvironmentStatusIcon(environment)}
                                  <h4 className="ml-2 text-md font-medium text-text-secondary">
                                    {t('environment', { name: environment.nom })}
                                  </h4>
                                </div>
                                <Link
                                  to={`/deployments/${environment.id}`}
                                  className="inline-flex items-center px-3 py-2 bg-accent-600 text-white text-sm font-medium rounded-lg hover:bg-accent-700 transition-colors shadow-card hover:shadow-md"
                                >
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                  </svg>
                                  {t('viewDeployments')}
                                </Link>
                              </div>

                              {environment.deployments.length === 0 ? (
                                <div className="text-center py-4 text-text-tertiary text-sm">
                                  <p>{t('noDeployedApp')}</p>
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                  {environment.deployments.map((deployment) => (
                                    <ApplicationStatus
                                      key={deployment.id}
                                      deployment={deployment}
                                      environmentId={environment.id}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
                )}
              </div>
          </div>
    </>
  );
};

export default StatusDashboard;